import {
  AdvancedSearchFieldConfig,
  AdvancedSearchFormField,
  isKnownFormField,
  isSubmittableFormField,
  StringFieldSearchConfig,
  SubmitableSortField,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import {
  advancedSearchToPrismaWhereContent,
  globalSearchToPrismaWhereContent,
  sortFieldsToPrismaOrderByContent,
} from "@/config/forms/advanced-search/advanced-search-prisma-conversion";
import { usePersistedState } from "@/hooks/usePersistedState";
import { Overwrite } from "@/lib/utils";
import { ReactNode, useCallback, useMemo } from "react";
import {
  AdvancedSearchContext,
  AdvancedSearchContextQueryData,
} from "./AdvancedSearchContext";

export const AdvancedSearchProvider = ({
  children,
  storageKey,
  persistInSearchParams,
}: {
  children: ReactNode;
  storageKey: string;
  persistInSearchParams?: boolean;
}) => {
  const [advancedSearchQuery, setInnerAdvancedSearchQuery] = usePersistedState<
    AdvancedSearchContextQueryData | undefined
  >(undefined, {
    storageKey: storageKey ? `${storageKey}.query` : undefined,
    urlKey:
      persistInSearchParams !== false && storageKey
        ? `${storageKey}-query`
        : undefined,
  });

  const [globalSearch, setGlobalSearch] = usePersistedState<{
    searchContent: string;
    stringConfig: AdvancedSearchFieldConfig<StringFieldSearchConfig>[];
  }>(
    { searchContent: "", stringConfig: [] },
    { storageKey: `${storageKey}.globalSearch` },
  );

  const [sortFields, setSortFields] = usePersistedState<
    SubmitableSortField[] | undefined
  >(undefined, {
    storageKey: `${storageKey}.sort`,
    urlKey:
      persistInSearchParams !== false && storageKey
        ? `${storageKey}-sort`
        : undefined,
  });

  const resetQuery = useCallback(() => {
    setInnerAdvancedSearchQuery(undefined);
    setGlobalSearch({ searchContent: "", stringConfig: [] });
  }, [setInnerAdvancedSearchQuery, setGlobalSearch]);

  const setAdvancedSearchQuery = (
    newQuery:
      | Overwrite<
          AdvancedSearchContextQueryData,
          { fields: AdvancedSearchFormField[] }
        >
      | undefined,
  ) => {
    if (newQuery == undefined) {
      setInnerAdvancedSearchQuery(undefined);
      return;
    }

    const mappedRequest = newQuery.fields
      .filter(isKnownFormField)
      .filter(isSubmittableFormField);

    const cleanQuery = { ...newQuery, fields: mappedRequest };

    setInnerAdvancedSearchQuery(cleanQuery);
  };

  const globalSearchQuery = useMemo(() => {
    return globalSearchToPrismaWhereContent(
      globalSearch.stringConfig,
      globalSearch.searchContent,
    );
  }, [globalSearch]);

  const resetSortFields = useCallback(() => {
    setSortFields(undefined);
  }, [setSortFields]);

  // transforms the inner states into a prisma ready "where" query !
  const prismaWhereQueryContent = useMemo(
    () => ({
      ...(advancedSearchQuery
        ? advancedSearchToPrismaWhereContent(
            advancedSearchQuery.fields,
            advancedSearchQuery.searchMode,
          )
        : {}),
      ...globalSearchQuery,
    }),
    [globalSearchQuery, advancedSearchQuery],
  );

  // transforms the inner states into a prisma ready "orderBy" query !
  const prismaOrderByQueryContent = useMemo(
    () => (sortFields ? sortFieldsToPrismaOrderByContent(sortFields) : []),
    [sortFields],
  );

  return (
    <AdvancedSearchContext.Provider
      value={{
        advancedSearchQuery,
        setAdvancedSearchQuery,
        resetQuery,
        setGlobalSearch,
        sortFields,
        setSortFields,
        resetSortFields,
        prismaWhereQueryContent,
        prismaOrderByQueryContent,
        globalSearch: globalSearch.searchContent,
      }}
    >
      {children}
    </AdvancedSearchContext.Provider>
  );
};
